import { AlertCircle, RefreshCw } from 'lucide-react'
import { Sentry } from '@/lib/sentry'
import { Button } from '@/components/ui/button'

interface Props {
  error: Error
  componentStack?: string
  eventId?: string | null
  resetError?: () => void
}

export function SentryErrorFallback({ error, eventId, resetError }: Props) {
  function handleReport() {
    Sentry.showReportDialog({ eventId: eventId ?? undefined })
  }

  return (
    <div className='flex min-h-screen flex-col items-center justify-center gap-6 px-4 text-center'>
      <div className='flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10'>
        <AlertCircle className='h-8 w-8 text-destructive' />
      </div>

      <div className='space-y-2'>
        <h1 className='text-2xl font-semibold tracking-tight'>
          Something went wrong
        </h1>
        <p className='max-w-md text-sm text-muted-foreground'>
          An unexpected error occurred. Our team has been notified automatically.
          You can try refreshing the page or report additional feedback.
        </p>
      </div>

      {import.meta.env.DEV && (
        <pre className='max-w-xl overflow-auto rounded-md bg-muted p-4 text-left text-xs text-muted-foreground'>
          {error.message}
        </pre>
      )}

      <div className='flex flex-wrap justify-center gap-3'>
        {resetError && (
          <Button onClick={resetError} variant='default' className='gap-2'>
            <RefreshCw className='h-4 w-4' />
            Try again
          </Button>
        )}
        {eventId && (
          <Button onClick={handleReport} variant='outline'>
            Report feedback
          </Button>
        )}
        <Button variant='ghost' onClick={() => window.location.assign('/')}>
          Go to home
        </Button>
      </div>

      {eventId && (
        <p className='text-xs text-muted-foreground'>
          Error ID: <span className='font-mono'>{eventId}</span>
        </p>
      )}
    </div>
  )
}
